import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Eye, TrendingUp, Users } from "lucide-react";
import { getCurrentThreadsMe } from "../../../api/AppLinkApi";

interface Props {
  engagement: any;
  postId?: string;
  platformPostId?: string;
}

export default function ThreadsInsightsPanel({
  engagement,
  postId: _postId,
  platformPostId,
}: Props) {
  const { t } = useTranslation();

  const [threadsHandle, setThreadsHandle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadThreadsProfile();
  }, []);

  const loadThreadsProfile = () => {
    setIsLoading(true);
    getCurrentThreadsMe()
      .then((data) => {
        // Get Threads handle/username from the profile
        const handle = data?.username || data?.threads_username || "";
        setThreadsHandle(handle ? `@${handle}` : "");
      })
      .catch(() => {
        setThreadsHandle("");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Extract insights from engagement data
  // Threads insights structure may vary, adjust based on actual API response
  const metrics = engagement?.getIn(["metrics", "data"])?.toJS() || [];

  // Try to find reach, impressions, and engagement metrics
  const findMetric = (name: string) => {
    return metrics.find((item: any) => item.name?.toLowerCase().includes(name.toLowerCase())) || {};
  };

  const reachMetric = findMetric("reach");
  const impressionsMetric = findMetric("impressions");
  const engagementMetric = findMetric("engagement");

  // Calculate total values from metric data
  const totalCount = (data: any) => {
    if (!data) return 0;
    if (data.value !== undefined) return data.value;
    if (data.values && Array.isArray(data.values)) {
      return data.values.reduce((total: number, item: any) => total + (item?.value || 0), 0);
    }
    // If no specific reach/impressions/engagement metrics, calculate from views/likes/replies
    return 0;
  };

  const reach = totalCount(reachMetric);
  const impressions = totalCount(impressionsMetric);
  const engagementValue = totalCount(engagementMetric);

  // Fallback: if no specific insights metrics, calculate from available metrics
  // For Threads, we might need to use views as impressions and sum of interactions as engagement
  const views = findMetric("views");
  const likes = findMetric("likes");
  const replies = findMetric("replies");
  const reposts = findMetric("reposts");

  // Use views as impressions if impressions not available
  const finalImpressions = impressions || totalCount(views);

  // Calculate engagement as sum of all interactions if engagement metric not available
  const finalEngagement =
    engagementValue || totalCount(likes) + totalCount(replies) + totalCount(reposts);

  // Use impressions as reach if reach not available (common fallback)
  const finalReach = reach || finalImpressions;

  // If no insights data, show loading or empty state
  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 mt-4 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 mt-4">
      {/* Header with Threads handle */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Thread Insights")} {threadsHandle && `– ${threadsHandle}`}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {t("Insights are fetched live from the Threads Graph API.")}
        </p>

        {/* Thread ID with copy button */}
        {platformPostId ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
            <span className="font-medium">{t("Thread ID")}:</span>
            <span className="font-mono text-gray-700">{platformPostId}</span>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800 transition-colors"
              title={t("Copy")}
              onClick={() => {
                if (navigator?.clipboard?.writeText) {
                  navigator.clipboard.writeText(String(platformPostId));
                }
              }}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Insights Metrics */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Reach */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Reach")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{finalReach.toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">
              {t("Unique accounts that saw this thread")}
            </p>
          </div>

          {/* Impressions */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Impressions")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {finalImpressions.toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t("Total number of times this thread was seen")}
            </p>
          </div>

          {/* Engagement */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-700">{t("Engagement")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {finalEngagement.toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">{t("Total interactions with this thread")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
